import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Clock, MapPin, Mail, ShieldCheck, Truck, CreditCard, HelpCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out via WhatsApp for the fastest response.
          </p>
        </div>

        {/* Main contact card */}
        <a
          href="https://wa.me/447440352811"
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-gradient-to-br from-green-600/20 to-green-500/5 rounded-2xl p-8 border border-green-500/30 hover:border-green-500/50 transition-all group mb-8"
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="bg-green-500/20 rounded-full w-20 h-20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
              <MessageCircle className="h-10 w-10 text-green-500" />
            </div>
            <div className="text-center md:text-left flex-1">
              <h3 className="text-white text-2xl font-semibold mb-2">Chat on WhatsApp</h3>
              <p className="text-zinc-400 mb-4">Get instant support and place orders directly via WhatsApp. Our team typically responds within 1-2 hours.</p>
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="h-4 w-4 mr-2" />
                Start Chat
              </Button>
            </div>
          </div>
        </a>

        {/* Info grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
            <Clock className="h-6 w-6 text-amber-500 mb-3" />
            <h4 className="text-white font-semibold mb-1">Response Time</h4>
            <p className="text-zinc-400 text-sm">Usually within 1-2 hours during business hours (9am-9pm GMT)</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
            <Truck className="h-6 w-6 text-amber-500 mb-3" />
            <h4 className="text-white font-semibold mb-1">Fast Delivery</h4>
            <p className="text-zinc-400 text-sm">UK delivery in 1-2 business days via tracked service</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
            <ShieldCheck className="h-6 w-6 text-amber-500 mb-3" />
            <h4 className="text-white font-semibold mb-1">Discreet Packaging</h4>
            <p className="text-zinc-400 text-sm">All orders shipped in plain, unmarked packaging</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
            <CreditCard className="h-6 w-6 text-amber-500 mb-3" />
            <h4 className="text-white font-semibold mb-1">Payment Methods</h4>
            <p className="text-zinc-400 text-sm">Bank transfer & cryptocurrency accepted</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
            <MapPin className="h-6 w-6 text-amber-500 mb-3" />
            <h4 className="text-white font-semibold mb-1">UK Based</h4>
            <p className="text-zinc-400 text-sm">Ship from UK warehouse for fast domestic delivery</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
            <Mail className="h-6 w-6 text-amber-500 mb-3" />
            <h4 className="text-white font-semibold mb-1">Minimum Order</h4>
            <p className="text-zinc-400 text-sm">£100 minimum order value required</p>
          </div>
        </div>

        {/* How to order section */}
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">How to Order</h2>
          <div className="space-y-4">
            {[
              { step: '1', title: 'Browse & Add to Cart', desc: 'Browse our products and add items to your cart. Minimum order is £100.' },
              { step: '2', title: 'Checkout via WhatsApp', desc: 'Click checkout and your order will be sent to us via WhatsApp automatically.' },
              { step: '3', title: 'Confirm & Pay', desc: 'We\'ll confirm stock availability and send you payment details (bank transfer or crypto).' },
              { step: '4', title: 'Fast Delivery', desc: 'Once payment is received, your order ships same/next day with tracked delivery.' }
            ].map((item, index) => (
              <div key={index} className="flex gap-4 items-start">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-black font-bold text-sm">{item.step}</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">{item.title}</h4>
                  <p className="text-zinc-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Link */}
        <Link
          to="/faq"
          className="block bg-zinc-900 rounded-2xl p-8 border border-zinc-800 hover:border-amber-500/30 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-amber-500/10 rounded-full w-14 h-14 flex items-center justify-center">
                <HelpCircle className="h-7 w-7 text-amber-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Frequently Asked Questions</h2>
                <p className="text-zinc-400">Find answers to common questions about orders, shipping, products and more</p>
              </div>
            </div>
            <ArrowRight className="h-6 w-6 text-zinc-500 group-hover:text-amber-500 transition-colors flex-shrink-0" />
          </div>
        </Link>
      </div>
    </div>
  );
};

export default ContactPage;
